import { HTTP_INTERCEPTORS, provideHttpClient } from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    ErrorHandler,
    LOCALE_ID,
    Provider,
    inject,
} from '@angular/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import {
    DateAdapter,
    ErrorStateMatcher,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    Router,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import {
    PRODUCT_SERVICE_TOKEN,
    ProductApiService,
} from '@crmConnect/features/products/services/api/product.service';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@ngneat/transloco';
import * as Sentry from '@sentry/angular';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideAuth } from './modules/auth/provider/auth.provider';
import { CleanStringsInterceptor } from './modules/shared/interceptors/clean-string-interceptor/clean-string.interceptor';
import { AppErrorStateMatcher } from './modules/shared/providers/error-state-matcher/error-state-matcher';
import { GlobalNavigationGuardService } from './modules/shared/providers/global-navigation/global-navigation.service';
import { AppMatPaginator } from './modules/shared/providers/mat-paginator/mat-paginator';

const providers: Provider[] = [
    {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
    {
        provide: APP_INITIALIZER,
        useFactory: () => () => {},
        deps: [Sentry.TraceService],
        multi: true,
    },
    {
        provide: MAT_DATE_LOCALE,
        useValue: 'es-HN',
    },
    {
        provide: LOCALE_ID,
        useValue: 'es-HN',
    },
    {
        provide: HTTP_INTERCEPTORS,
        useClass: CleanStringsInterceptor,
        multi: true,
    },
    {
        provide: ErrorStateMatcher,
        useClass: AppErrorStateMatcher,
    },
    {
        provide: MatPaginatorIntl,
        useClass: AppMatPaginator,
    },
    {
        provide: PRODUCT_SERVICE_TOKEN,
        useClass: ProductApiService,
    },
    {
        provide: APP_INITIALIZER,
        useFactory:
            (globalNavigationGuardService: GlobalNavigationGuardService) =>
            () =>
                globalNavigationGuardService.checkAccess('/'),
        deps: [GlobalNavigationGuardService],
        multi: true,
    },
];

export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpClient(),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),

        {
            provide: DateAdapter,
            useClass: LuxonDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: 'D',
                },
                display: {
                    dateInput: 'DDD',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },

        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'en',
                        label: 'English',
                    },
                    {
                        id: 'tr',
                        label: 'Turkish',
                    },
                ],
                defaultLang: 'en',
                fallbackLang: 'en',
                reRenderOnLangChange: true,
                prodMode: true,
            },
            loader: TranslocoHttpLoader,
        }),
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                const translocoService = inject(TranslocoService);
                const defaultLang = translocoService.getDefaultLang();
                translocoService.setActiveLang(defaultLang);

                return () => firstValueFrom(translocoService.load(defaultLang));
            },
            multi: true,
        },

        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: mockApiServices,
            },
            fuse: {
                layout: 'classy',
                scheme: 'light',
                screens: {
                    xxs: '280px',
                    xs: '470px',
                    sm: '600px',
                    smd: '768px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                    xxl: '1920px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-gray',
                        name: 'Gray',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        ...providers,
    ],
};
